import React, { useState } from "react";
import axios from "axios";
import { FaFilePdf, FaTimes } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getReceiveHistoryOrder,
  toggleReceiveHistoryModal,
} from "../../features/orders/ordersSlice";

import styles from "./OrdersFormModal.module.scss";

const ReceiveHistoryModal = () => {
  const dispatch = useAppDispatch();
  const order = useAppSelector(getReceiveHistoryOrder);

  const [loadingPdfId, setLoadingPdfId] = useState<number | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = (hours % 12 || 12).toString().padStart(2, "0");
    return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  const handleViewPdf = async (transactionId: number) => {
    setLoadingPdfId(transactionId);

    try {
      const token = localStorage.getItem("authToken");
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";

      const response = await axios.get(
        `${apiBaseUrl}/v1/admin/receive-transactions/${transactionId}/pdf`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: process.env.REACT_APP_API_KEY,
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");

      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (error) {
      console.error("Error fetching PDF:", error);
      alert("PDF લોડ કરવામાં ભૂલ થઈ. કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
    }

    setLoadingPdfId(null);
  };

  const handleCloseModal = () => {
    dispatch(toggleReceiveHistoryModal(null));
  };

  if (!order) return null;

  const transactions = order.receive_transactions || [];
  const isInSet = order.item?.isinset || false;

  return (
    <div className={styles.OrdersFormModal}>
      <div className="relative h-[100vh] w-[100vw] overflow-y-scroll lg:overflow-auto top-0 lg:top-10 lg:mx-auto p-5 border lg:w-[500px] lg:max-h-[80vh] shadow-lg lg:rounded-md bg-white">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-primary-1">
            પરત હિસ્ટ્રી - ઓર્ડર #{order.id}
          </h3>
          <button
            onClick={handleCloseModal}
            className="text-gray-500 hover:text-gray-700 p-2"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Order Info */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">પાર્ટી:</span>
              <span className="font-semibold text-primary-1 ml-2">
                {order.party?.firm_name || "-"}
              </span>
            </div>
            <div>
              <span className="text-gray-500">આઈટમ:</span>
              <span className="font-semibold text-primary-1 ml-2">
                {order.item?.name || "-"}
                {isInSet && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full ml-1">
                    સેટ
                  </span>
                )}
              </span>
            </div>
            <div>
              <span className="text-gray-500">ઓર્ડર નંગ:</span>
              <span className="font-semibold text-primary-1 ml-2">
                {order.qty}
              </span>
            </div>
            <div>
              <span className="text-gray-500">કુલ ડિલિવરી:</span>
              <span className="font-semibold text-primary-1 ml-2">
                {order.delivered_qty}
              </span>
            </div>
          </div>
        </div>

        {/* Transactions List */}
        {transactions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            કોઈ પરત ટ્રાન્ઝેક્શન નથી
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm font-semibold text-gray-600 mb-2">
              કુલ ટ્રાન્ઝેક્શન: {transactions.length}
            </div>
            {transactions.map((tx, index) => (
              <div
                key={tx.id}
                className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
              >
                {/* Header Row */}
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-2">
                    <span className="bg-primary-1 text-white text-sm font-bold px-2.5 py-1 rounded-full">
                      #{transactions.length - index}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {formatDate(tx.created_at)}
                    </span>
                  </div>
                  <button
                    onClick={() => handleViewPdf(tx.id)}
                    disabled={loadingPdfId === tx.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-red-100 transition-colors"
                  >
                    <FaFilePdf className="text-sm" />
                    {loadingPdfId === tx.id ? "લોડ..." : "PDF"}
                  </button>
                </div>

                {/* Details Grid */}
                <div className="bg-gray-50 rounded-xl p-3">
                  {isInSet ? (
                    <div className="space-y-2">
                      {/* Kesh Details (if present) */}
                      {((tx.qty_field_2 !== null && tx.qty_field_2 > 0) ||
                        (tx.weight_field_2 !== null &&
                          tx.weight_field_2 > 0)) && (
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200">
                          <div className="flex justify-between">
                            <span className="text-gray-500 text-sm">
                              કેશ નંગ:
                            </span>
                            <span className="text-primary-1 font-semibold text-sm">
                              {tx.qty_field_2 || 0}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500 text-sm">
                              કેશ વજન:
                            </span>
                            <span className="text-primary-1 font-semibold text-sm">
                              {(tx.weight_field_2 || 0).toFixed(3)} કિ.
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Patta Details */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex justify-between">
                          <span className="text-gray-500 text-sm">
                            પટ્ટા નંગ:
                          </span>
                          <span className="text-primary-1 font-semibold text-sm">
                            {tx.qty_field_1}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 text-sm">
                            પટ્ટા વજન:
                          </span>
                          <span className="text-primary-1 font-semibold text-sm">
                            {tx.weight_field_1.toFixed(3)} કિ.
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">નંગ:</span>
                        <span className="text-primary-1 font-semibold text-sm">
                          {tx.qty_field_1}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">વજન:</span>
                        <span className="text-primary-1 font-semibold text-sm">
                          {tx.weight_field_1.toFixed(3)} કિલો
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Close Button */}
        <div className="mt-4 text-center">
          <button
            className="mx-auto shadow rounded-3xl text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
            type="button"
            onClick={handleCloseModal}
          >
            બંધ કરો
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiveHistoryModal;
